import React, { useEffect, useState } from "react";

import { TextInput } from "@dooglie/ui";
import { SeatIncrease, SeatIncreaseRequired } from "../API";
import MemberRow from "./MemberRow";
import type { SettingsPageProps } from "./OrgActions";
import TimezoneCard from "./TimezoneCard";

const UsersPage: React.FC<SettingsPageProps> = ({ data, busy, run, actions }) => {
  const seats = data.seats!;
  const members = data.members || [];
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [filter, setFilter] = useState("");
  const [showUnseated, setShowUnseated] = useState(false);
  // The admin's working tick set, seeded from the server and only sent on Save —
  // so they can move several seats around and see the cost of the whole change
  // once, rather than being asked to buy on each individual tick.
  const [seated, setSeated] = useState<Set<string>>(
    () => new Set(members.filter((m) => m.has_seat).map((m) => m.email)),
  );
  const [pendingIncrease, setPendingIncrease] = useState<SeatIncrease | null>(null);

  const serverSeated = members.filter((m) => m.has_seat).map((m) => m.email);
  const dirty =
    seated.size !== serverSeated.length || serverSeated.some((e) => !seated.has(e));

  // Every mutation reloads from the server, which is the source of truth — adopt
  // what it says so a saved (or externally changed) roster stops reading dirty.
  const serverKey = [...serverSeated].sort().join(",");
  useEffect(() => {
    setSeated(new Set(serverSeated));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);
  const pct = seats.paid > 0 ? Math.min(100, Math.round((seated.size / seats.paid) * 100)) : 0;

  const needle = filter.trim().toLowerCase();
  const shown = members
    .filter(
      (m) =>
        !needle ||
        m.email.toLowerCase().includes(needle) ||
        (m.name || "").toLowerCase().includes(needle),
    )
    .sort((a, b) =>
      (a.name || a.email).localeCompare(b.name || b.email, undefined, { sensitivity: "base" }),
    );
  // Grouped by what the server says, not the working tick set, so a row stays
  // put while the admin ticks it. Everyone the firm has is listed — CRM sync
  // adds every recruiter — so the people without a seat fold away unless a
  // filter is looking for them.
  const shownSeated = shown.filter((m) => m.has_seat);
  const shownUnseated = shown.filter((m) => !m.has_seat);
  const unseatedOpen = showUnseated || needle !== "";

  const toggle = (memberEmail: string) =>
    setSeated((current) => {
      const next = new Set(current);
      if (next.has(memberEmail)) {
        next.delete(memberEmail);
      } else {
        next.add(memberEmail);
      }
      return next;
    });

  // Saving over the cap comes back as SeatIncreaseRequired rather than silently
  // charging them; we quote it, and only a confirmed re-send buys the seats.
  const save = (approve_increase?: boolean) =>
    run(async () => {
      try {
        await actions.setSeats([...seated], approve_increase);
        setPendingIncrease(null);
      } catch (e: unknown) {
        if (e instanceof SeatIncreaseRequired) {
          setPendingIncrease(e.increase);
          return;
        }
        throw e;
      }
    });

  const submitAdd = () =>
    run(async () => {
      await actions.addUser(email.trim(), name.trim());
      setEmail("");
      setName("");
      setShowAdd(false);
    });

  return (
    <section className="dg-page">
      <div className="dg-eyebrow dg-ph-eye">Admin</div>
      <div className="dg-ph-title">Users</div>
      <div className="dg-ph-sub">
        Everyone Dooglie knows at your firm. Only people with a seat can use Dooglie.
      </div>

      <TimezoneCard data={data} busy={busy} run={run} actions={actions} />

      {seats.over_seat && (
        <div className="dg-banner">
          You have given out more seats than you are paying for. Purchase more
          seats, or take some back, to stay compliant.
        </div>
      )}

      {dirty && (
        <div className="dg-banner dg-banner-row">
          <span>You have unsaved seat changes.</span>
          <button
            className="dg-btn dg-btn-primary dg-btn-sm"
            disabled={busy}
            onClick={() => save()}
          >
            Save changes
          </button>
        </div>
      )}

      <div className="dg-seat-hero">
        <div style={{ flex: 1 }}>
          <div className="dg-seat-num">
            {seated.size}
            <span className="dg-seat-denom">/{seats.paid || seats.contracted}</span>
          </div>
          <div className="dg-seat-caption">
            seats given out{dirty ? " (unsaved)" : ""} · {seats.members_total} members total
          </div>
          <div className="dg-seat-bar">
            <div className="dg-seat-fill" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="dg-btn dg-btn-inverse"
            disabled={busy}
            onClick={() => run(() => actions.purchaseSeats(1))}
          >
            + Buy a seat
          </button>
          <button
            className="dg-btn dg-btn-inverse-soft"
            // Floored on what the SERVER has seated, not the local tick set: the
            // backend refuses to bill for fewer seats than are held, so an
            // unticked-but-unsaved row would let this through and fail there.
            // Unsaved changes block it outright — save first, then reduce.
            disabled={
              busy ||
              dirty ||
              seats.paid <= Math.max(seats.contracted, serverSeated.length)
            }
            title={
              dirty
                ? "Save your seat changes first"
                : seats.paid <= Math.max(seats.contracted, serverSeated.length)
                  ? "You can't pay for fewer seats than you've given out, or fewer than your agreed minimum"
                  : undefined
            }
            onClick={() => run(() => actions.reduceSeats(1))}
          >
            − Remove a seat
          </button>
        </div>
      </div>

      {pendingIncrease && (
        <div className="dg-add-form">
          <div style={{ fontWeight: 700 }}>
            This will increase your cap by {pendingIncrease.additional}{" "}
            {pendingIncrease.additional === 1 ? "seat" : "seats"}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="dg-btn dg-btn-primary" disabled={busy} onClick={() => save(true)}>
              Confirm and buy
            </button>
            <button
              className="dg-btn dg-btn-ghost"
              onClick={() => setPendingIncrease(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="dg-add-form">
          <div style={{ fontWeight: 700, marginBottom: 14 }}>Add a user</div>
          <div className="dg-fgrid">
            <div className="dg-field">
              <label>Name (optional)</label>
              <TextInput
                field="users.add_name"
                hint="A person's name"
                inputProps={{ "aria-label": "Name (optional)" }}
                value={name}
                placeholder="Filled from their sign-in"
                onValueChange={setName}
              />
            </div>
            <div className="dg-field">
              <label>Email address</label>
              <TextInput
                field="users.add_email"
                hint="An email address"
                type="email"
                inputProps={{ "aria-label": "Email address" }}
                value={email}
                placeholder="name@company.com"
                onValueChange={setEmail}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="dg-btn dg-btn-primary" disabled={busy || !email.trim()} onClick={submitAdd}>
              Add user
            </button>
            <button className="dg-btn dg-btn-ghost" onClick={() => setShowAdd(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="dg-card">
        <div className="dg-card-head">
          <div className="dg-card-title">All users</div>
          <TextInput
            className="dg-filter"
            field="users.filter"
            hint="A name or email address"
            inputProps={{ "aria-label": "Filter by name or email" }}
            value={filter}
            placeholder="Filter by name or email"
            onValueChange={setFilter}
          />
          <button
            className="dg-btn dg-btn-primary dg-btn-sm"
            disabled={busy || !dirty}
            onClick={() => save()}
          >
            Save changes
          </button>
          <button className="dg-btn dg-btn-ghost dg-btn-sm" onClick={() => setShowAdd(true)}>
            + Add user
          </button>
        </div>
        <div>
          {[...shownSeated, ...(unseatedOpen ? shownUnseated : [])].map((m) => (
            <MemberRow
              key={m.email}
              member={m}
              seated={seated.has(m.email)}
              onToggleSeat={() => toggle(m.email)}
              busy={busy}
              run={run}
              actions={actions}
            />
          ))}
          {shownUnseated.length > 0 && needle === "" && (
            <div style={{ padding: "10px 20px" }}>
              <button
                className="dg-btn dg-btn-ghost dg-btn-sm"
                onClick={() => setShowUnseated((open) => !open)}
              >
                {showUnseated
                  ? "Hide people without a seat"
                  : `Show ${shownUnseated.length} ${
                      shownUnseated.length === 1 ? "person" : "people"
                    } without a seat`}
              </button>
            </div>
          )}
          {shown.length === 0 && (
            <div className="dg-note" style={{ padding: "14px 20px" }}>
              No members match “{filter}”.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default UsersPage;
